/**
 * draftModel.ts
 *
 * Pure helpers for the draft Workspace that the "New job / New loan / New title
 * search" button opens. A draft is not a stored item: it is the Workspace route
 * with the reserved id `new`, shown in Intake Mode with every other Mode locked
 * until the item is created (Requirements 2.3, 3.5).
 *
 * - TR: `IntakeTR` collects everything and creates the search on submit.
 * - DD/BA: the draft opens Intake itself (no create form). The item needs a
 *   real job id before loans or documents can be uploaded, so the job is
 *   created lazily on the first real action with `defaultDraftName` (and the
 *   borrower name for BA when known), then the URL is replaced with
 *   `getCreatedItemPath`.
 *
 * No React and no I/O.
 */

import { JobType } from '../graphql/types';
import type { CreateJobInput } from '../graphql/types';
import { buildCreateBAJobPayload, buildCreateJobPayload } from '../services/apiPayloadBuilder';
import { FEATURE_HOME_PATH } from './modes';

/** Reserved route id of a draft. Real job ids are generated, so never `new`. */
export const DRAFT_JOB_ID = 'new';

export const MAX_DRAFT_NAME_LENGTH = 100;

/** Unit of work per Feature (design.md: job for DD, loan for BA, search for TR). */
const UNIT: Readonly<Record<JobType, string>> = {
  [JobType.DD]: 'job',
  [JobType.BA]: 'loan',
  [JobType.TR]: 'title search',
};

export function isDraftJobId(jobId: string | null | undefined): boolean {
  return jobId === DRAFT_JOB_ID;
}

/** Route of the draft Workspace for a Feature, opened in Intake Mode. */
export function getDraftPath(feature: JobType): string {
  return `${FEATURE_HOME_PATH[feature]}/${DRAFT_JOB_ID}?mode=intake`;
}

/** Route of a created item's Workspace, opened in Intake Mode. */
export function getCreatedItemPath(feature: JobType, jobId: string): string {
  return `${FEATURE_HOME_PATH[feature]}/${jobId}?mode=intake`;
}

/** Unit of work in words: "job", "loan" or "title search". */
export function getUnitOfWork(feature: JobType): string {
  return UNIT[feature];
}

/** Top_Bar title of a draft: "New job", "New loan", "New title search". */
export function getDraftTitle(feature: JobType): string {
  return `New ${UNIT[feature]}`;
}

/** Default name offered for a new DD/BA item, as the old modal did. */
export function defaultDraftName(displayName: string, now: Date = new Date()): string {
  return `${displayName} Job ${now.toLocaleDateString()}`;
}

/** Same rule as the old modal: required, at most 100 characters. */
export function validateDraftName(name: string): string | null {
  const trimmed = name.trim();
  if (!trimmed) return 'Name is required';
  if (trimmed.length > MAX_DRAFT_NAME_LENGTH) {
    return `Name must be ${MAX_DRAFT_NAME_LENGTH} characters or less`;
  }
  return null;
}

/** BA needs the borrower's name (same rule as `validateLoanDataForm`). */
export function validateDraftBorrower(borrowerName: string): string | null {
  return borrowerName.trim() ? null : 'Borrower name is required';
}

/**
 * Payload to create the DD/BA item behind a draft. The BA loan details beyond
 * the borrower are filled in on Intake, which saves them to the loan.
 */
export function buildDraftCreatePayload(
  feature: JobType.DD | JobType.BA,
  name: string,
  borrowerName = ''
): CreateJobInput {
  const trimmed = name.trim();
  if (feature === JobType.BA) {
    return buildCreateBAJobPayload(trimmed, feature, '', borrowerName.trim(), '', '', []);
  }
  return buildCreateJobPayload(trimmed, feature);
}
